import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';

// Types defined locally to avoid shared package dependency issues
export interface Supplier {
  id: string;
  name: string;
  code: string;
  isVerified: boolean;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  parentId?: string;
  order: number;
}

export interface ProductListItem {
  id: string;
  name: string;
  slug: string;
  price: number;
  images: string[];
  brand?: string | null;
  createdAt: string;
  rating?: number;
  supplier?: Supplier;
  category?: Category;
}

export interface Product extends ProductListItem {
  description?: string;
  attributes?: Record<string, unknown>;
  stock: number;
  supplierUrl?: string;
  reviewCount?: number;
}

export interface Banner {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl?: string;
  order: number;
  isActive: boolean;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Query keys
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...productKeys.lists(), filters] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (slug: string) => [...productKeys.details(), slug] as const,
  search: (query: string) => [...productKeys.all, 'search', query] as const,
};

export const categoryKeys = {
  all: ['categories'] as const,
  detail: (slug: string) => [...categoryKeys.all, slug] as const,
};

export const brandKeys = {
  all: ['brands'] as const,
};

export const bannerKeys = {
  all: ['banners'] as const,
};

// Products - Backend returns data array directly with meta for pagination
// Note: api.get returns ApiResponse<T> where T is what we request
// For products list, the backend returns { data: products[], meta: pagination }

// Transformed response for components
interface ProductsResponse {
  products: ProductListItem[];
  pagination: PaginationMeta;
}

interface ProductFilters {
  page?: number;
  limit?: number;
  categoryId?: string;
  supplierId?: string;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

export function useProducts(filters: ProductFilters = {}) {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.append(key, String(value));
    }
  });

  return useQuery({
    queryKey: productKeys.list(filters),
    queryFn: () => api.get<ProductListItem[]>(`/products?${queryParams.toString()}`),
    select: (response): ProductsResponse => ({
      products: response.data || [],
      pagination: response.meta || { page: 1, limit: 20, total: 0, totalPages: 0 },
    }),
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: productKeys.detail(slug),
    queryFn: () => api.get<Product>(`/products/${slug}`),
    select: (data) => data.data,
    enabled: !!slug,
  });
}

export function useProductSearch(query: string) {
  return useQuery({
    queryKey: productKeys.search(query),
    queryFn: () => api.get<ProductListItem[]>(`/products/search?q=${encodeURIComponent(query)}`),
    select: (response): ProductsResponse => ({
      products: response.data || [],
      pagination: response.meta || { page: 1, limit: 20, total: 0, totalPages: 0 },
    }),
    enabled: query.length >= 2,
  });
}

// Categories
export function useCategories() {
  return useQuery({
    queryKey: categoryKeys.all,
    queryFn: () => api.get<Category[]>('/categories'),
    select: (data) => data.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useCategoryProducts(slug: string, page = 1, brand?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (brand) params.append('brand', brand);

  return useQuery({
    queryKey: [...categoryKeys.detail(slug), page, brand],
    queryFn: () => api.get<{ category: Category; products: ProductsResponse }>(
      `/categories/${slug}?${params.toString()}`
    ),
    select: (data) => data.data,
    enabled: !!slug,
  });
}

// Banners
export function useBanners() {
  return useQuery({
    queryKey: bannerKeys.all,
    queryFn: () => api.get<Banner[]>('/banners'),
    select: (data) => data.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Brands
export function useBrands() {
  return useQuery({
    queryKey: brandKeys.all,
    queryFn: () => api.get<string[]>('/products/brands'),
    select: (data) => data.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Wishlist
export const wishlistKeys = {
  all: ['wishlist'] as const,
};

export function useWishlist(enabled = true) {
  return useQuery({
    queryKey: wishlistKeys.all,
    queryFn: () => api.get<ProductListItem[]>('/wishlist'),
    select: (data) => data.data,
    enabled,
  });
}

export function useAddToWishlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => api.post(`/wishlist/${productId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
    },
  });
}

export function useRemoveFromWishlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => api.delete(`/wishlist/${productId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
    },
  });
}

// Reviews
export const reviewKeys = {
  product: (productId: string) => ['reviews', productId] as const,
};

interface Review {
  id: string;
  rating: number;
  comment: string;
  userName: string;
  createdAt: string;
}

export function useProductReviews(productId: string) {
  return useQuery({
    queryKey: reviewKeys.product(productId),
    queryFn: () => api.get<Review[]>(`/reviews/product/${productId}`),
    select: (data) => data.data,
    enabled: !!productId,
  });
}

export function useAddReview(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { rating: number; comment: string }) =>
      api.post(`/reviews/product/${productId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.product(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
